<template>
	<div>
		<RichTextBox
			ref="rich_text_box_ref"
			v-on="$listeners"
			v-model="localValue"
			:isFocused.sync="isFocused"
			:isLongText="isLongText"
			:textAreaMode="textAreaMode"
			:inputFieldHint="placeholder"
			:mode="mode"
			:options="options"
			:nodeId="nodeId"
			:multipleSelection="multipleSelection"
			:readonly="readonly"
			:inputSize="inputSize"
			:showDataMenu="showDataMenu"
			:richTextFormat="richTextFormat"
			:dontShowInsertData="dontShowInsertData"
			:disableEnter="disableEnter"
			:hasDelete="hasDelete"
			@click="onInputClick"
			@content-updated="onContentUpdate"
		/>
		<transition v-if="!dontShowInsertData" name="fromBottomMenu" mode="out-in">
			<div class="menu-container" v-click-outside="{ handler: onClickOutside, include: getAbsoluteMenuElements }">
				<DataInputMenuAbsoluteMenuWrapper v-model="showDataMenu" v-bind="absoluteMenuProps">
					<DataInputMenu
						v-if="mode === 1"
						ref="data_input_menu"
						:tab.sync="cTab"
						:localValue.sync="localValue"
						:showMenu.sync="showDataMenu"
						:nodeId="editNode.id"
						:hasExternalSource="false"
						:tabItems="tabItems"
						:showMenuFlag="showDataMenu"
						:curTabCode="curTabCode"
						:preventTables="preventTables"
						:valuesTypes="valuesTypes"
						:didOpenBySlash="didOpenBySlash"
						@update-previous-step-localValue="updateValueFromMenu"
						@update-text-localValue="updateValueFromMenu"
						@close="showDataMenu = false"
					/>
					<DataInputMenu
						v-else-if="mode === 2"
						v-on="$listeners"
						:localValue.sync="localValue"
						:showMenu.sync="showDataMenu"
						:nodeId="nodeId"
						:showMenuFlag="showDataMenu"
						:curTabCode="PREVIOUS_STEP_TABLE_OUTPUT"
						:tabItems="tabItems"
						:valuesTypes="valuesTypes"
						:fieldParams="formattedFieldParams"
						:preventTables="preventTables"
						:numOfRows="numOfRows"
						:didOpenBySlash="didOpenBySlash"
						@close="showDataMenu = false"
						@set-value-from-table-source="updateValueFromMenu"
						@update-previous-step-localValue="updateValueFromMenu"
						@update-text-localValue="updateValueFromMenu"
					/>
				</DataInputMenuAbsoluteMenuWrapper>
			</div>
		</transition>
		<div v-if="!dontShowInsertData" class="mt-n7 pb-7">
			<div v-if="isNoValue && showOpenMenuButton && !noPrefix" :style="prefixStyle ? prefixStyle : ''" class="prefix">
				Type '/' to insert data
			</div>
			<div v-if="showOpenMenuButton && !noSuffix" :style="suffixStyle ? suffixStyle : ''" class="suffix-icons">
				<OpenMenuButton @open="showDataMenu = true" />
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import { cloneDeep, debounce } from 'lodash';

import DataInputMenuService from './DataInputMenu/data-input-menu-service';

import OpenMenuButton from '@/components/BlockEditor/BlockEditorParams/inputsComponents/assets/OpenMenuButton';
import DataInputMenuAbsoluteMenuWrapper from '@/components/common/DataInputMenuAbsoluteMenuWrapper';

import { EXTERNAL_SOURCE_TAB_KEY, THIS_FLOW_TAB_KEY, PREVIOUS_STEP_TABLE_OUTPUT } from './DataInputMenu/helpers/data-input-menu.const';

// mixins that responsible on reposition DataInputMenu triggered by IntersectionObserver or scroll event
import IntersectionObserverMixin from '@/components/BlockEditor/BlockEditorParams/DataInputMenus/mixins/IntersectionObserverMixin';
import ScrollResizeListenersMixin from '@/components/BlockEditor/BlockEditorParams/DataInputMenus/mixins/ScrollResizeListenersMixin';

export default {
	name: 'CombinedInputFieldDataMenu',
	mixins: [IntersectionObserverMixin, ScrollResizeListenersMixin],
	props: {
		value: { type: String },
		disableEnter: { type: Boolean },
		textAreaMode: { type: Boolean },
		noSuffix: { type: Boolean },
		noPrefix: { type: Boolean },
		placeholder: { type: String },
		mode: { type: Number, default: 1 },
		options: { type: Array, default: () => [] },
		nodeId: { type: Number },
		multipleSelection: { type: Boolean },
		readonly: { type: Boolean, default: false },
		numOfRows: { type: Number },
		inputSize: { type: String },
		valuesTypes: { type: Array, validator: DataInputMenuService.valueTypesValidator, default: () => [] },
		prefixStyle: { type: Object },
		suffixStyle: { type: Object },
		richTextFormat: { type: String },
		dontShowInsertData: { type: Boolean },
		isLongText: { type: Boolean },
		dontOpenOnSlash: { type: Boolean },
		hasDelete: { type: Boolean },
		preventTables: { type: Boolean },
	},
	components: {
		OpenMenuButton,
		DataInputMenu: () => import('./DataInputMenu/DataInputMenu'),
		RichTextBox: () => import('@/components/common/RichTextBox/RichTextBox'),
		DataInputMenuAbsoluteMenuWrapper,
	},
	data() {
		return {
			PREVIOUS_STEP_TABLE_OUTPUT,
			isFocused: false,
			showDataMenu: false,
			cTab: 0,
			localValueDisplay: '',
			positionX: null,
			positionY: null,
			menuDirection: 'bottom',
			didOpenBySlash: false,
		};
	},
	watch: {
		isFocused(n) {
			if (n) {
				if (this.readonly) {
					this.calcAbsoluteMenuPosition();
					this.showDataMenu = true;
				}
			}
			this.$emit('isFocused', n);
		},
		showDataMenu(n) {
			this.isFocused = n;
			if (n) {
				// IntersectionObserver logic - managed in IntersectionObserverMixin
				this._createIntersectionObserver(this._handleIntersectionEntries);
				this.$data._intersectionObserver.observe(this.getRichTextBoxElement());

				this._listenToEvents({ targetElement: this.getRichTextBoxElement() });
			} else {
				// IntersectionObserver logic - managed in IntersectionObserverMixin
				this._removeIntersectionObserver();

				this._removeEventListeners();

				if (this.didOpenBySlash) {
					this.getRichTextBox()?.focus();
				}
				this.didOpenBySlash = false;
				if (this.readonly) {
					this.getRichTextBox()?.blur();
				}
			}
		},
	},
	created() {
		const updatePrefill = function () {
			this.localField.metadata.options['prefill'].value = this.localValue;
		};
		this.updatePrefill = debounce(updatePrefill.bind(this), 100);
	},
	mounted() {
		this.setLocalField();
	},
	computed: {
		...mapGetters('selectedNode', ['editNode']),
		...mapGetters('widgetEditor', ['editField']),
		tabItems() {
			if (this.mode === 2) {
				return [
					{ tab: THIS_FLOW_TAB_KEY, title: 'This Flow ' },
					{ tab: PREVIOUS_STEP_TABLE_OUTPUT, title: 'Table' },
				];
			}
			return [{ tab: THIS_FLOW_TAB_KEY, title: 'This Flow ' }];
		},
		formattedFieldParams() {
			const allKeys = this.options.map((option) => option.key);
			const allValues = this.options.map((option) => option.example);
			const exampleObj = {};
			allKeys.map((key, index) => (exampleObj[key] = allValues[index]));
			return [exampleObj];
		},
		curTab() {
			return this.tabItems[this.cTab];
		},
		curTabCode() {
			return this.curTab.tab;
		},
		localValue: {
			set(value) {
				this.$emit('update:value', value);
			},
			get() {
				return this.value;
			},
		},
		absoluteMenuProps() {
			return {
				positionX: this.positionX,
				positionY: this.positionY,
				[this.menuDirection]: true,
				'close-on-content-click': false,
				'close-on-click': false,
			};
		},
		showOpenMenuButton() {
			return this.isFocused && !this.showDataMenu;
		},
		isNoValue() {
			return !this.localValue || this.localValue === '';
		},
	},
	methods: {
		focus() {
			this.getRichTextBox()?.focus();
		},
		onContentUpdate(key) {
			if (key === '/' && !this.dontOpenOnSlash && !this.dontShowInsertData) {
				this.showDataMenu = true;
				this.didOpenBySlash = true;
			} else {
				this.showDataMenu = false;
			}
			this.$emit('content-updated', key);
		},

		updateLocalValue(newVal) {
			if (this.curTabCode === EXTERNAL_SOURCE_TAB_KEY) return;
			this.localValueDisplay = '';
			this.localValue = newVal;
		},
		updateValueFromMenu(value) {
			this.showDataMenu = false;
			if (!this.localValue || this.localValueDisplay) {
				this.localValue = value;
			} else {
				this.getRichTextBox()?.addValue(value, this.didOpenBySlash);
			}
			this.localValueDisplay = '';
		},
		getRichTextBox() {
			return this.$refs.reg || this.$refs.rich_text_box_ref;
		},
		getRichTextBoxElement() {
			return this.getRichTextBox()?.$el;
		},

		onInputClick() {
			if (this.isNoValue && !this.dontShowInsertData) {
				this.showDataMenu = true;
			}
		},
		onClickOutside() {
			if (!this.isFocused) {
				this.showDataMenu = false;
			}
		},
		setLocalField() {
			this.localField = cloneDeep(this.editField);
		},
		updatePrefill() {}, // declare it and override it on created
		getAbsoluteMenuElements() {
			return DataInputMenuService.getDataInputMenuAbsoluteElements();
		},
		calcAbsoluteMenuPosition(args) {
			let { targetElement } = args || {};
			if (!targetElement) {
				targetElement = this.getRichTextBoxElement();
			}
			const menuRect = DataInputMenuService.getMenuRect({ ...args, targetElement });
			this.menuDirection = menuRect.dir;
			this.positionX = menuRect.x;
			this.positionY = menuRect.y;
		},
		_handleIntersectionEntries(entries) {
			entries.forEach(this._handleIntersectionEntry);
		},
		_handleIntersectionEntry(entry) {
			// observer is observing one element at a time, so if it's not intersecting with it, we should hide menu
			if (!entry.isIntersecting) {
				this.showDataMenu = false;
			}
		},
		/**
		 * onScroll handler
		 * @param {*} args
		 */
		_onScroll(args) {
			this.calcAbsoluteMenuPosition(args);
		},
		/**
		 * onResize handler
		 * @param {*} args
		 */
		_onResize(args) {
			this.calcAbsoluteMenuPosition(args);
		},
		_listenToEvents(args) {
			// sign for scroll and resize events - managed in ScrollResizeListenersMixin
			this._listenOnScroll(args, this._onScroll);
			this._listenOnResize(args, this._onResize);
		},
		_removeEventListeners() {
			this._removeOnScrollListener();
			this._removeOnResizeListener();
		},
	},
};
</script>

<style scoped lang="scss">
.menu-container {
	width: 100%;
	z-index: 8;
	position: absolute;
}
.suffix-icons {
	right: 10px;
	position: absolute;
	z-index: 1;
}
.prefix {
	left: 10px;
	font-size: 14px;
	margin-top: -2px;
	z-index: 1;
	position: absolute;
	color: var(--insert-data-placeholder);
}
</style>
